import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import PredictiveLeads from '../components/PredictiveLeads';
import {
  Users,
  UserPlus,
  CheckCircle,
  Fire,
  ThermometerSimple,
  Snowflake,
  TrendUp,
  Clock,
} from '@phosphor-icons/react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';

const API = `/api`;

const KPICard = ({ title, value, icon: Icon, trend, color, testId }) => {
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-5 card-hover"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            color === 'blue'
              ? 'bg-blue-50 text-blue-600'
              : color === 'green'
              ? 'bg-emerald-50 text-emerald-600'
              : color === 'red'
              ? 'bg-red-50 text-red-600'
              : color === 'orange'
              ? 'bg-orange-50 text-orange-600'
              : 'bg-slate-50 text-slate-600'
          }`}
        >
          <Icon size={24} weight="duotone" />
        </div>
        {trend && (
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              trend.positive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
            }`}
          >
            <TrendUp size={12} />
            {trend.value}
          </div>
        )}
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500 mt-1">{title}</p>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, {
        withCredentials: true,
      });
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const statusColors = {
    novo: '#3B82F6',
    ia_atendendo: '#8B5CF6',
    qualificado: '#10B981',
    quente: '#EF4444',
    morno: '#F59E0B',
    frio: '#64748B',
    em_atendimento: '#06B6D4',
    proposta: '#8B5CF6',
    venda_realizada: '#10B981',
    perdido: '#1E293B',
  };

  const statusData = stats?.status_distribution?.map((item) => ({
    name: item._id || 'Indefinido',
    value: item.count,
  })) || [];

  const sourceData = stats?.source_distribution?.map((item) => ({
    name: item._id || 'Indefinido',
    value: item.count,
  })) || [];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="dashboard-title">
            Dashboard Executivo
          </h1>
          <p className="text-slate-600 mt-1">Visão geral do funil comercial</p>
        </div>
        <button
          onClick={() => navigate('/novo-lead')}
          className="px-5 py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
        >
          Cadastrar Novo Lead
        </button>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <KPICard
            title="Total de Leads"
            value={stats?.total_leads || 0}
            icon={Users}
            color="blue"
            trend={{ positive: true, value: '+12%' }}
            testId="kpi-total-leads"
          />
          <KPICard
            title="Leads Recebidos"
            value={stats?.received_leads || stats?.new_leads || 0}
            icon={UserPlus}
            color="green"
            testId="kpi-new-leads"
          />
          <KPICard
            title="Qualificados"
            value={stats?.qualified_leads || 0}
            icon={CheckCircle}
            color="green"
            testId="kpi-qualified-leads"
          />
          <KPICard
            title="Conversão"
            value={`${stats?.conversion_rate || 0}%`}
            icon={TrendUp}
            color="blue"
            trend={{ positive: true, value: '+5%' }}
            testId="kpi-conversion-rate"
          />
        </div>

        {/* Classification Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <Fire size={20} weight="duotone" className="text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.hot_leads || 0}</p>
                <p className="text-sm text-slate-500">Leads Quentes</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                <ThermometerSimple size={20} weight="duotone" className="text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.warm_leads || 0}</p>
                <p className="text-sm text-slate-500">Leads Mornos</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
                <Snowflake size={20} weight="duotone" className="text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.cold_leads || 0}</p>
                <p className="text-sm text-slate-500">Leads Frios</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Status Distribution */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Distribuição por Status
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#64748b" />
                <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="value" fill="#1E3A8A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Source Distribution */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Origem dos Leads</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sourceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => entry.name}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sourceData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={[
                        '#1E3A8A',
                        '#10B981',
                        '#F59E0B',
                        '#EF4444',
                        '#8B5CF6',
                        '#06B6D4',
                        '#EC4899',
                      ][index % 7]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* AI Predictive Leads */}
        <PredictiveLeads />

        {/* Broker Ranking */}
        {stats?.broker_ranking && stats.broker_ranking.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Ranking de Corretores
            </h3>
            <div className="space-y-3">
              {stats.broker_ranking.map((broker, index) => (
                <div
                  key={broker._id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0
                          ? 'bg-yellow-100 text-yellow-700'
                          : index === 1
                          ? 'bg-slate-200 text-slate-700'
                          : index === 2
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span className="font-medium text-slate-900">{broker.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">{broker.count} leads</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;